import { useTranslation } from '@dagens/frontend-i18n';
import { Button, PageHeader } from '@dagens/carrot';
import { OnboardingHistoryKeys } from '../../../types/Producer';
import { Category, ProducerProduct } from '../../../types/Product';
import ProductGroup from './ProductGroup';
import Theme from '@carrot-deprecated/theme';
import { ButtonLink } from '_common/components/button/Button.style';
import InContextOnboardingMessage from '_common/components/in-context-onboarding-message/InContextOnboardingMessage';
import MultiProductSeasonCalendar from '_common/components/season/MultiProductSeasonCalendar';
import Icon from '_common/components/utils/Icon';
import { useAppSelector } from '_common/hooks/reduxHooks';
import usePageView from '_common/hooks/usePageView';
import { splitProductsBySaleStatus } from '_producer/reducers/products';
import { Page } from '@components/page';
import { RootState } from 'store';
import { byClosestDate } from 'utils/array';
import { MY_PRODUCTS_PAGE_VIEW } from 'utils/mixpanel';
import REQ, { combineReqs } from 'utils/REQ';

const producerProductToExport = (
  product: ProducerProduct,
  producerCategories: Category[]
) => {
  const category = producerCategories.find(
    category => category._id === product.categoryId
  );
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    capacities: _capacities,
    categoryId,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    certificationIds: _certificationIds,
    certifications,
    description,
    forSaleStatus,
    image,
    name,
    prices,
    pricing,
    processedState,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    producer: _producer,
    seasonCalendar,
    temperatureZone,
    type,
    deleted
  } = product;
  return {
    category: {
      _id: categoryId,
      name: category?.name,
      name_en: category?.name_en,
      name_nb: category?.name_nb,
      name_da: category?.name_da,
      isNonFood: category?.isNonFood
    },
    certifications,
    description,
    forSaleStatus,
    image,
    name,
    prices,
    pricing,
    processedState,
    seasonCalendar: seasonCalendar.map(period => ({
      from: period.from,
      ...('to' in period && { to: period.to })
    })),
    temperatureZone,
    type,
    deleted
  };
};

const useDownloadProductsAsJSON = ({
  activeProducts,
  forSale,
  onRequest,
  notForSale
}: {
  activeProducts: ProducerProduct[] | undefined;
  forSale: ProducerProduct[] | undefined;
  onRequest: ProducerProduct[] | undefined;
  notForSale: ProducerProduct[] | undefined;
}) => {
  const { producerCategories } = useAppSelector(
    ({ producerCategories }: RootState) => {
      return {
        producerCategories: producerCategories.categories
      };
    }
  );

  const downloadProductsAsJSON = () => {
    const productsAsJSON = JSON.stringify(
      {
        activeProducts: activeProducts?.map(p =>
          producerProductToExport(p, producerCategories)
        ),
        forSale: forSale?.map(p =>
          producerProductToExport(p, producerCategories)
        ),
        onRequest: onRequest?.map(p =>
          producerProductToExport(p, producerCategories)
        ),
        notForSale: notForSale?.map(p =>
          producerProductToExport(p, producerCategories)
        )
      },
      null,
      2
    );
    const blob = new Blob([productsAsJSON], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  };

  return downloadProductsAsJSON;
};

type Props = {
  activeProducts?: ProducerProduct[];
  forSale?: ProducerProduct[];
  onRequest?: ProducerProduct[];
  notForSale?: ProducerProduct[];
};

const ProducerProductsPage = ({
  activeProducts,
  forSale,
  onRequest,
  notForSale
}: Props) => {
  const { t } = useTranslation();
  const byClosestToToday = byClosestDate(new Date());
  usePageView(MY_PRODUCTS_PAGE_VIEW);

  const { hasAddedFirstProduct } = useAppSelector(({ auth }: RootState) => {
    return {
      hasAddedFirstProduct: Boolean(
        auth.onboardingHistory?.[OnboardingHistoryKeys.addedFirstProduct]
      )
    };
  });

  const downloadProductsAsJSON = useDownloadProductsAsJSON({
    activeProducts,
    forSale,
    onRequest,
    notForSale
  });

  return (
    <Page>
      <PageHeader
        title={t('producer:MyProducts')}
        subtitle={t('producer:productHeaderNumbProducts', {
          count: activeProducts?.length ?? 0
        })}
      />
      <InContextOnboardingMessage
        message={t('producer:products.addYourFirstProduct')}
        showStep={!hasAddedFirstProduct}
      />

      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        {!hasAddedFirstProduct ? (
          <ButtonLink variant="primary" to="/my-products/add">
            <Icon icon="add" $size="s" mr={Theme.Spacings.xxs} />{' '}
            {t('producer:productAddProduct')}
          </ButtonLink>
        ) : (
          <ButtonLink variant="secondary" to="/my-products/add">
            <Icon icon="add" $size="s" fill="white" mr={Theme.Spacings.xxs} />{' '}
            {t('producer:productAddNewProduct')}
          </ButtonLink>
        )}

        <Button variant="secondary" onClick={downloadProductsAsJSON}>
          <Icon
            icon="products"
            $size="s"
            fill="white"
            mr={Theme.Spacings.xxs}
          />{' '}
          {t('producer:ExportProducts')}
        </Button>
      </div>

      {activeProducts && activeProducts.length > 0 && (
        <MultiProductSeasonCalendar
          products={activeProducts?.sort(byClosestToToday)}
          isProducer
        />
      )}
      {forSale && forSale.length > 0 && (
        <ProductGroup header={t('common:FOR_SALE')} products={forSale} />
      )}

      {onRequest && onRequest.length > 0 && (
        <ProductGroup header={t('common:ON_REQUEST')} products={onRequest} />
      )}

      {notForSale && notForSale.length > 0 && (
        <ProductGroup header={t('common:NOT_FOR_SALE')} products={notForSale} />
      )}
    </Page>
  );
};

const ProducerProductsPageFetcher = () => {
  const { req, activeProducts, forSale, notForSale, onRequest } =
    useAppSelector(
      ({
        producerProducts: { items = [], req: productsReq },
        consumers: { req: consumersReq }
      }) => {
        const { forSale, onRequest, notForSale, activeProducts } =
          splitProductsBySaleStatus(items);

        return {
          req: combineReqs([productsReq, consumersReq]),
          activeProducts,
          forSale,
          onRequest,
          notForSale
        };
      }
    );

  if (req !== REQ.SUCCESS) {
    return <Page.Status req={req} />;
  }

  return (
    <ProducerProductsPage
      activeProducts={activeProducts}
      forSale={forSale}
      onRequest={onRequest}
      notForSale={notForSale}
    />
  );
};

export default ProducerProductsPageFetcher;
